import React, { useState, useEffect } from 'react';
import { Search, Command } from 'lucide-react';
import BreadcrumbBar, { BreadcrumbSegment } from './BreadcrumbBar';
import ThemeToggle from './ThemeToggle';
import RoleLensChip from './RoleLensChip';
import AccountMenu from './AccountMenu';
import SupportReportButton from '../../components/support/SupportReportButton';

interface TopbarProps {
  /** Breadcrumb segments showing current location */
  breadcrumbs?: BreadcrumbSegment[];
  /** Current user role */
  userRole: string;
  /** User display name */
  userName: string;
  /** User email */
  userEmail: string;
  /** Current theme */
  theme: 'dark' | 'light';
  /** Theme toggle callback */
  onThemeToggle: () => void;
  /** Navigation callback */
  onNavigate: (path: string) => void;
  /** Sign out callback */
  onSignOut: () => void;
  /** Command palette toggle callback (cmd+K) */
  onCommandPalette?: () => void;
  /** Settings callback */
  onSettings?: () => void;
}

/**
 * Topbar — 58px header bar.
 *
 * Layout:
 * - Left: Wordmark from BRAND_NAME (never hardcoded)
 * - Center: BreadcrumbBar showing Group > Case > Stage > Panel
 * - Right: cmd+K search input, theme toggle, role chip, account menu
 */
export default function Topbar({
  breadcrumbs = [],
  userRole,
  userName,
  userEmail,
  theme,
  onThemeToggle,
  onNavigate,
  onSignOut,
  onCommandPalette,
  onSettings,
}: TopbarProps) {
  // Handle cmd+K keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onCommandPalette?.();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onCommandPalette]);

  return (
    <header
      className="
        h-[58px] px-4
        bg-[var(--color-bg-base)]
        border-b border-[var(--color-hairline)]
        flex items-center justify-between
        sticky top-0 z-40
      "
    >
      {/* Left: Breadcrumbs (wayfinding). The brand wordmark lives in the left rail,
          not here — a second top-bar wordmark duplicated it and wrapped on narrow
          widths (K3 design gate, 2026-10-08). */}
      <div className="hidden md:flex flex-1 justify-start">
        <BreadcrumbBar segments={breadcrumbs} onNavigate={onNavigate} />
      </div>

      {/* Right: Actions */}
      <div className="flex items-center gap-2">
        {/* Search Input (cmd+K) */}
        <button
          onClick={onCommandPalette}
          className="
            hidden sm:flex items-center gap-2
            px-3 py-1.5 rounded-md
            bg-[var(--color-bg-surface)]
            border border-[var(--color-border)]
            text-[var(--color-text-muted)]
            hover:border-[var(--color-border-sub)]
            hover:text-[var(--color-text-secondary)]
            transition-colors
          "
        >
          <Search className="w-4 h-4" />
          <span className="text-sm">Search</span>
          <kbd className="
            hidden lg:inline-flex items-center gap-0.5
            px-1.5 py-0.5 rounded
            bg-[var(--color-bg-elevated)]
            text-[var(--color-text-dim)] text-xs
            border border-[var(--color-border)]
          ">
            <Command className="w-3 h-3" />
            <span>K</span>
          </kbd>
        </button>

        {/* Report an issue — quiet, in-app support reporter (captures route/case
            context automatically). Muted chrome; no orange on the trigger. */}
        <div className="hidden sm:block">
          <SupportReportButton />
        </div>

        {/* Theme Toggle */}
        <ThemeToggle theme={theme} onToggle={onThemeToggle} />

        {/* Role Lens Chip */}
        <div className="hidden sm:block">
          <RoleLensChip role={userRole} />
        </div>

        {/* Account Menu */}
        <AccountMenu
          userName={userName}
          userEmail={userEmail}
          onSignOut={onSignOut}
          onSettings={onSettings}
        />
      </div>
    </header>
  );
}
